import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, type ReactElement } from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import {
  Datepicker,
  type DatepickerProps
} from './Elements/Datepicker/Datepicker'
import {
  errorMessage,
  inputField,
  inputLabel,
  inputLeadingIcon,
  inputLeadingText,
  inputWrapper
} from './TextInput.css'

type DateInputProps = InputInFormProps &
  Omit<DatepickerProps, 'onChange' | 'currentDate'> & {
    label?: string
    name?: string
    startName?: string
    endName?: string
    defaultDate?: Date | null
    onChange?: (date: Date | null) => void
    error?: null | { message: string }
    required?: boolean
    wrapper?: boolean
    leadingIcon?: IconDefinition
    leadingText?: string
  }

export const DateInput = ({
  register,
  label,
  name,
  startName,
  endName,
  defaultDate,
  onChange,
  error = null,
  required,
  wrapper = true,
  leadingIcon,
  leadingText,
  watch,
  setValue,
  ...props
}: DateInputProps): ReactElement => {
  const [date, setDate] = React.useState<Date | null>(defaultDate ?? null)

  useEffect(() => {
    if (defaultDate !== undefined) {
      setDate(defaultDate)
    }
  }, [defaultDate])

  const datepickerProps: Omit<DatepickerProps, 'onChange' | 'currentDate'> =
    props

  let inputValue = undefined

  if (
    register !== undefined &&
    setValue !== undefined &&
    name !== undefined &&
    watch !== undefined
  ) {
    register(name)
    inputValue = watch(name)

    if (startName && endName) {
      if (startName === name) {
        datepickerProps.selectsStart = true
      }
      if (endName === name) {
        datepickerProps.selectsEnd = true
        datepickerProps.minDate = watch(startName)
      }
      datepickerProps.startDate = watch(startName)
      datepickerProps.endDate = watch(endName)
    }
  }

  const onDateChange = (date: Date | null) => {
    setDate(date)
    if (setValue !== undefined && name !== undefined) {
      setValue(name, date)
    }
    onChange?.(date)
  }

  useEffect(() => {
    if (inputValue !== undefined) {
      setDate(inputValue)
    }
  }, [inputValue])

  const Input = (
    <>
      {leadingIcon && (
        <div className={inputLeadingIcon}>
          <FontAwesomeIcon icon={leadingIcon} />
        </div>
      )}
      {leadingText && <div className={inputLeadingText}>{leadingText}</div>}
      <Datepicker
        currentDate={date}
        onChange={onDateChange}
        {...datepickerProps}
      />
    </>
  )

  if (!wrapper) {
    return <div className={inputField}>{Input}</div>
  }

  return (
    <div className={inputWrapper}>
      {label && (
        <label htmlFor={name} className={inputLabel}>
          {label}
        </label>
      )}
      <div
        className={inputField}
        aria-invalid={error !== null ? 'true' : 'false'}
        aria-label={label}
      >
        {Input}
      </div>
      {error !== null && (
        <div className={errorMessage} role="alert">
          {error.message}
        </div>
      )}
    </div>
  )
}
